import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { selectHasPermission } from "../../store/slices/authSlice";
import {
  fetchClientMovementForms,
  deleteClientMovementForm,
} from "../../store/slices/clientMovementFormSlice";

const ClientMovementRequestFormList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { caseId, patientId } = location.state || {};

  const canManage = useSelector((state) =>
    selectHasPermission(state, "client-movement-request-form-manage")
  );
  const canCreate = useSelector((state) =>
    selectHasPermission(state, "client-movement-request-form-create")
  );
  const canEdit = useSelector((state) =>
    selectHasPermission(state, "client-movement-request-form-edit")
  );
  const canDelete = useSelector((state) =>
    selectHasPermission(state, "client-movement-request-form-delete")
  );

  const { clientMovementForms, isLoading, error } = useSelector((state) => state.clientMovementForms);
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchClientMovementForms(patientId));
  }, [dispatch, patientId]);

  const handleAdd = () => {
    navigate("/phase03/escort-form/add", { state: location.state });
  };

  const handleEdit = (id) => {
    navigate(`/phase03/escort-form/edit/${id}`, { state: location.state });
  };

  const handlePrint = (id) => {
    navigate(`/phase03/escort-form/print/${id}`, { state: location.state });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Client Movement Request Form?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteClientMovementForm(id)).unwrap();
        dispatch(fetchClientMovementForms(patientId));
        Swal.fire("Deleted!", "Client Movement Request Form has been deleted.", "success");
      } catch (err) {
        Swal.fire("Error!", err || "Failed to delete record.", "error");
      }
    }
  };

  const filteredRecords = (clientMovementForms || []).filter((r) =>
    Object.values(r).some((v) =>
      String(v ?? "")
        .toLowerCase()
        .includes(filterText.toLowerCase())
    )
  );

  const totalRows = filteredRecords.length;
  const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Client Movement Request Form Records", 14, 10);

    const columns = [
      "#",
      "Client Name",
      "Request Date",
      "Purpose",
      "Movement Date",
      "Movement Time",
      "Duration (hrs)",
      "Family Member",
    ];

    const rows = filteredRecords.map((r, i) => [
      i + 1,
      r.client_name ?? "-",
      r.date_of_movement_request ?? "-",
      r.purpose_of_visit ?? "-",
      r.requested_date ?? "-",
      r.requested_time ?? "-",
      r.estimated_duration ?? "-",
      r.family_member_name ?? "-",
    ]);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 20,
    });

    doc.save("client_movement_request_records.pdf");
  };

  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
          >
            {[5, 10, 15, 20].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(Math.min(totalPages, currentPage + 1), rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      name: "S.No",
      cell: (_, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "70px",
    },
    { name: "Client Name", selector: (r) => r.client_name, sortable: true },
    { name: "Request Date", selector: (r) => r.date_of_movement_request, sortable: true },
    { name: "Purpose of Visit", selector: (r) => r.purpose_of_visit, sortable: true },
    { name: "Movement Date", selector: (r) => r.requested_date, sortable: true },
    { name: "Movement Time", selector: (r) => r.requested_time },
    { name: "Duration (hrs)", selector: (r) => r.estimated_duration },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1 justify-content-center">
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => handlePrint(row.id)}
            title="Print Preview"
          >
            <i className="ri-printer-line"></i>
          </button>
          {canEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEdit(row.id)}
              title="Edit Form"
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {canDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.id)}
              title="Delete Form"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      width: "160px",
    },
  ];

  if (!canManage) {
    return (
      <Navigate
        to="/dashboard"
        state={{ error: "You do not have permission to manage Client Movement Request forms." }}
        replace
      />
    );
  }

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <div className="dropdown">
                <button
                  className="btn btn-label-primary btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ri-download-2-line me-1"></i> Export
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button className="dropdown-item" onClick={handleExportPDF}>
                      <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                    </button>
                  </li>
                  <li>
                    <CSVLink
                      data={filteredRecords}
                      filename="client_movement_request_records.csv"
                      className="dropdown-item"
                    >
                      <i className="ri-file-excel-line me-1 text-success"></i> CSV
                    </CSVLink>
                  </li>
                </ul>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                style={{ width: "200px" }}
              />
              {canCreate && (
                <button className="btn btn-primary btn-sm" onClick={handleAdd}>
                  <i className="ri-add-line me-1"></i> Add Escort Form
                </button>
              )}
            </div>
          </div>

          <div className="card-body p-0">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={filteredRecords}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationComponent={(props) => (
                  <CustomPagination
                    {...props}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    onChangeRowsPerPage={(size) => {
                      setRowsPerPage(size);
                      setCurrentPage(1);
                    }}
                  />
                )}
                noDataComponent={
                  <div className="p-4 text-center text-muted">
                    No Escort forms found. Click 'Add Escort Form' to create one.
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ClientMovementRequestFormList;
